(ns app.front.view.admin.commands.regenerate-pdf
  (:require [app.front.ui :as ui]
            [app.front.ui.table :as ui-table]
            [app.front.ui.updated :as ui-updated]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(rf/reg-event-fx ::fetch-stuck-pdfs
  (fn [{:keys [db]} _]
    {:db (assoc db ::permits-loading? true ::bills-loading? true)
     :fx [[:comm/command {:command          :admin/get-stuck-permits
                          :method           :get
                          :on-success       [::fetch-stuck-permits.success]
                          :finally-dispatch [::loading-ready ::permits-loading?]}]
          [:comm/command {:command          :admin/get-stuck-bills
                          :method           :get
                          :on-success       [::fetch-stuck-bills.success]
                          :finally-dispatch [::loading-ready ::bills-loading?]}]]}))

(rf/reg-event-db ::fetch-stuck-permits.success
  (fn [db [_ status resp]]
    (cond-> db
      (= status 200) (assoc ::stuck-permits resp))))

(rf/reg-event-db ::fetch-stuck-bills.success
  (fn [db [_ status resp]]
    (cond-> db
      (= status 200) (assoc ::stuck-bills resp))))

(rf/reg-event-db ::loading-ready
  (fn [db [_ k]]
    (dissoc db k)))

(rf/reg-event-fx ::re-send-verdict-pdf-request
  (fn [{:keys [db]} [_ verdict-id]]
    {:comm/command {:command :admin/re-send-verdict-pdf-request
                    :method  :post
                    :data    (util/mmap verdict-id)
                    :notify  {:success "PDF generation successfully requested again"}}
     :db           (update db ::stuck-permits (partial filter #(-> % :db/id (not= verdict-id))))}))

(rf/reg-event-fx ::regenerate-receipt-or-invoice
  (fn [{:keys [db]} [_ command bill-id]]
    {:comm/command {:command command
                    :method  :post
                    :data    {:bill-id (futil/parse-int bill-id)}
                    :notify  {:success "Receipt / invoice regeneration started"}}
     :db           (-> db
                       (update ::stuck-bills (fn [bills]
                                               (->> bills
                                                    (map (fn [{:keys [:db/id] :as bill}]
                                                           (if (= id bill-id)
                                                             (assoc bill
                                                                    (case command
                                                                      :admin/regenerate-bill :bill/bill-pdf-document
                                                                      :admin/regenerate-receipt :bill/receipt-document)
                                                                    :generating)
                                                             bill)))))))}))

(rf/reg-sub ::stuck-permits
  (fn [db _]
    (->> db
         ::stuck-permits
         (sort-by :started >))))

(rf/reg-sub ::stuck-bills
  (fn [db _]
    (->> db
         ::stuck-bills
         (sort-by :started >))))

(rf/reg-sub ::permits-loading? :-> ::permits-loading?)
(rf/reg-sub ::bills-loading? :-> ::bills-loading?)

;; Sign-off PDF regeneration
(rf/reg-event-db ::set-sign-off-workspace-id
  (fn [db [_ workspace-id]]
    (assoc db ::sign-off-workspace-id workspace-id)))

(rf/reg-sub ::sign-off-workspace-id
  :-> ::sign-off-workspace-id)

(rf/reg-event-db ::show-sign-off-regenerate-modal
  (fn [db _]
    (assoc db ::sign-off-regenerate-modal-open? true)))

(rf/reg-event-db ::hide-sign-off-regenerate-modal
  (fn [db _]
    (dissoc db ::sign-off-regenerate-modal-open?)))

(rf/reg-sub ::sign-off-regenerate-modal-open?
  :-> ::sign-off-regenerate-modal-open?)

(rf/reg-event-fx ::regenerate-sign-off-pdf
  (fn [{:keys [db]} _]
    (let [workspace-id (::sign-off-workspace-id db)]
      {:dispatch [:comm/command-monitored
                  {:command :admin/regenerate-sign-off-pdf
                   :method  :post
                   :data    {:workspace-id workspace-id}
                   :notify  {:success "Sign-off PDF regenerated successfully"}}]
       :db       (dissoc db ::sign-off-regenerate-modal-open?)})))

(def phase-entity->group-name
  (comp :group/name :domain/authority-group :entity/parent))

(defn age-minutes [element]
  [ui-updated/updated {:ts (:started element)}])

(defn bill-actions [{:keys      [:db/id :entity/parent :domain/state]
                     :bill/keys [bill-pdf-document
                                 receipt-document
                                 bill-pdf-document-generating?
                                 receipt-document-generating?]}]
  [:div.flex.flex-wrap
   (when (and (not receipt-document)
              receipt-document-generating?
              (= state :bill.state/paid))
     [ui/modal-confirmation-button
      {:tr                  (<sub [:tr])
       :id                  (str id "-receipt")
       :trigger-button-text "Regen receipt"
       :trigger-button-opts {:size :small}
       :on-yes-click        #(>evt [::regenerate-receipt-or-invoice :admin/regenerate-receipt id])}
      [:span
       "Do you want to re-send receipt generation request for bill "
       id]])
   (when (and (not bill-pdf-document)
              bill-pdf-document-generating?
              (-> parent
                  :entity/parent
                  :domain/authority-group
                  :group/features
                  set
                  (contains? :group.feature/generate-bill-pdf)))
     [ui/modal-confirmation-button
      {:tr                  (<sub [:tr])
       :id                  (str id "-invoice")
       :trigger-button-text "Regen invoice"
       :trigger-button-opts {:size       :small
                             :class-name "ml-2"}
       :on-yes-click        #(>evt [::regenerate-receipt-or-invoice :admin/regenerate-bill id])}
      [:span
       "Do you want to re-send bill generation request for bill "
       id]])])

(def table-link
  {:description "Open entity-lookup"
   :make-href   (fn [{:keys [:db/id]}]
                  (href/href :view/admin.entity-lookup-with-param {:search-term (str id)}))})

(defn regen-receipt []
  (r/with-let [*receipt-id      (r/atom nil)
               *invoice-bill-id (r/atom nil)]
    [:section
     [:h2 "Regenerate receipt or invoice"]

     (if (<sub [::bills-loading?])
       (cc/inline-loading {:description "Loading bill data..."})
       [ui-table/modern-responsive-table
        {:items (<sub [::stuck-bills])
         :link  table-link
         :cols  [["Workspace ID" (comp :workspace/id :entity/parent :entity/parent)]
                 ["Group" (comp phase-entity->group-name :entity/parent)]
                 ["Stuck since" age-minutes]
                 ["" bill-actions]]}])

     [ui/help "If the above table for some reason does not contain your bill, you can manually enter the entity id below. Please inform the dev team if these inputs are needed, so we can fix the tool.\nEnsure that the document should actually be generated - there could be an error in the tool showing cases which should not contain an invoice at all."]

     [:div.flex.items-end.mt-8
      [:div.w-1-2.mr-3
       [ui/text-input {:id          "receipt-id"
                       :light       true
                       :value       @*receipt-id
                       :on-change   #(reset! *receipt-id (futil/tval %))
                       :placeholder "A long number, use entity lookup to find it"
                       :label-text  "Bill entity id"}]]
      [:div
       (cc/button {:kind     :primary
                   :disabled (nil? @*receipt-id)
                   :size     :field
                   :on-click #(>evt [::regenerate-receipt-or-invoice :admin/regenerate-receipt @*receipt-id])}
                  "Regenerate receipt")]]
     [:div.flex.items-end.mt-6
      [:div.w-1-2.mr-3
       [ui/text-input {:id          "invoice-bill-id"
                       :light       true
                       :value       @*invoice-bill-id
                       :on-change   #(reset! *invoice-bill-id (futil/tval %))
                       :placeholder "A long number, use entity lookup to find it"
                       :label-text  "Bill entity id"}]]
      [:div
       (cc/button {:kind     :primary
                   :disabled (nil? @*invoice-bill-id)
                   :size     :field
                   :on-click #(>evt [::regenerate-receipt-or-invoice :admin/regenerate-bill @*invoice-bill-id])}
                  "Regenerate invoice")]]]))

(defn reset-btn [{:keys [:db/id :verdict/municipal-permit-number]}]
  [ui/modal-confirmation-button
   {:tr                  (<sub [:tr])
    :id                  (str id)
    :trigger-button-text "Re-send message"
    :on-yes-click        #(>evt [::re-send-verdict-pdf-request id])}
   [:span
    "Do you want to re-send the PDF generation request to the queue for permit "
    municipal-permit-number]])

(defn stuck-permits []
  [acommon/section
   {:title "Stuck permits"}

   [ui/help "You can re-send permit PDF generation requests here. The 'Message ID' column may help match the error with backend logs. Please file a trouble ticket whenever this tool is needed, if not yet filed.\nUse with caution, especially if the permit has been stuck for a long time (i.e. check that the situation is not already resolved some other way)."]
   (if (<sub [::permits-loading?])
     (cc/inline-loading {:description "Loading permit data..."})
     [ui-table/modern-responsive-table
      {:items (<sub [::stuck-permits])
       :cols  [["Permit #" :verdict/municipal-permit-number]
               ["Group" (comp phase-entity->group-name :entity/parent)]
               ["Issuer" :verdict/issuer]
               ["Message ID" (comp str :message/id first :message/_target-entity)]
               ["Stuck since" age-minutes]
               ["" reset-btn]]
       :link  table-link
       :tr    (<sub [:tr])}])])


(defn regen-sign-off-pdf []
  (let [workspace-id (<sub [::sign-off-workspace-id])
        modal-open?  (<sub [::sign-off-regenerate-modal-open?])
        processing?  (<sub [:comm/command-processing? :admin/regenerate-sign-off-pdf])
        tr           (<sub [:tr])]
    [:section
     [:h2 "Regenerate sign-off PDF"]

     [ui/help "Regenerate a sign-off PDF that is missing from storage. The application must have been fully signed off. This does not overwrite an existing PDF."]

     [:div.flex.items-end.mt-8
      [:div.w-1-2.mr-3
       [ui/text-input {:id          "sign-off-workspace-id"
                       :light       true
                       :value       (or workspace-id "")
                       :on-change   #(>evt [::set-sign-off-workspace-id (futil/tval %)])
                       :placeholder "e.g. BP-2024-00123"
                       :label-text  "Workspace ID"}]]
      [:div
       (cc/button {:kind     :danger
                   :disabled (or (empty? workspace-id) processing?)
                   :size     :field
                   :on-click #(>evt [::show-sign-off-regenerate-modal])}
                  (if processing?
                    "Regenerating..."
                    "Regenerate sign-off PDF"))]]

     ;; Confirmation modal
     [ui/confirm-modal
      {:id           "regenerate-sign-off-pdf-modal"
       :tr           tr
       :open?        (boolean modal-open?)
       :on-close     #(>evt [::hide-sign-off-regenerate-modal])
       :on-yes-click #(>evt [::regenerate-sign-off-pdf])
       :modal-title  "Regenerate sign-off PDF"
       :dangerous?   true}
      [:div.mb-3
       "This will generate a new sign-off PDF for workspace "
       [:strong workspace-id]
       " Continue?"]]]))


(defn view [match]
  [acommon/admin-page
   (util/mmap {:title "Re-send PDF generation request"} match)
   [stuck-permits]
   [regen-receipt]
   [regen-sign-off-pdf]])

(def route
  {:name        :view/admin.commands.regenerate-pdf
   :view        view
   :controllers [{:start (fn [_] (>evt [::fetch-stuck-pdfs]))}]})
